import React, { useEffect, useRef, useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format, isBefore, isSameDay } from 'date-fns';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Calendar } from './ui/calendar';
import { Popover, PopoverAnchor, PopoverContent } from './ui/popover';
import { cn } from '../lib/utils';

// ONE pop-up for a first and last day (a friend's feedback via Rob, 02/11/2026: "when you select
// the first date it closes and forces you to click the end date … like skyscanner.net").
// Two months side by side (one on a phone). The first click sets the first day and the pop-up stays
// open; the second sets the last day and closes it (Rob 1A) — the SAME day twice for one day (Rob 2B).
// A click before the first day starts again from there. Dates read DD/MM/YYYY (CONVENTIONS.md).
// Used by the Request leave box, /leave/new and the three no-leave dates boxes (Rob 3A, 02/11/2026).
export default function DateRangeField({
  start, end, onChange, disabled, startLabel = 'Start date', endLabel = 'End date',
  startTestId, endTestId, endDisabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState('start');   // which day the next click sets
  const [hover, setHover] = useState(null);
  const [wide, setWide] = useState(true);
  const anchorRef = useRef(null);
  useEffect(() => {
    const m = window.matchMedia('(min-width: 640px)');
    const set = () => setWide(m.matches);
    set(); m.addEventListener('change', set);
    return () => m.removeEventListener('change', set);
  }, []);

  const openFor = (which) => { setPicking(which); setOpen(true); };
  const pick = (day) => {
    if (!day || (disabled && disabled(day))) return;
    if (picking === 'start' || !start || isBefore(day, start)) {
      onChange({ start: day, end: null });
      setPicking('end');
      return;
    }
    onChange({ start, end: day });
    setOpen(false);
    setHover(null);
  };

  const preview = picking === 'end' && start && !end && hover && !isBefore(hover, start)
    ? { from: start, to: hover } : null;
  const show = (d) => (d ? format(d, 'EEE dd/MM/yyyy') : 'Pick a date');

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setHover(null); }}>
      <PopoverAnchor asChild>
        <div ref={anchorRef} className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{startLabel}</Label>
            <Button type="button" variant="outline"
              className={cn('w-full justify-start text-left font-normal input-hive',
                !start && 'text-muted-foreground', open && picking === 'start' && 'ring-2 ring-primary')}
              data-testid={startTestId} onClick={() => openFor('start')}>
              <CalendarIcon className="mr-2 h-4 w-4" />{show(start)}
            </Button>
          </div>
          <div className="space-y-2">
            <Label>{endLabel}</Label>
            <Button type="button" variant="outline"
              className={cn('w-full justify-start text-left font-normal input-hive',
                !end && 'text-muted-foreground', open && picking === 'end' && 'ring-2 ring-primary')}
              data-testid={endTestId} disabled={endDisabled} onClick={() => openFor('end')}>
              <CalendarIcon className="mr-2 h-4 w-4" />{show(end)}
            </Button>
          </div>
        </div>
      </PopoverAnchor>
      <PopoverContent className="w-auto max-w-[calc(100vw-1rem)] p-0" align="start" collisionPadding={8}
        // The two day boxes open it, so a press on them isn't "outside"; and the Request leave box
        // keeps focus to itself, which would otherwise shut the pop-up the moment it opens.
        onFocusOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => { if (anchorRef.current && anchorRef.current.contains(e.target)) e.preventDefault(); }}>
        <Calendar
          mode="range"
          numberOfMonths={wide ? 2 : 1}
          weekStartsOn={1}
          selected={preview || (start ? { from: start, to: end || undefined } : undefined)}
          onDayClick={pick}
          onDayMouseEnter={(d) => setHover(d)}
          onDayMouseLeave={() => setHover(null)}
          disabled={disabled}
          defaultMonth={start || new Date()}
          initialFocus
        />
        <div className="px-4 pb-3 -mt-1 text-[12.5px] text-muted-foreground">
          {picking === 'start'
            ? 'Pick your first day off.'
            : 'Now pick your last day — press the same day again if it\'s just one.'}
        </div>
      </PopoverContent>
    </Popover>
  );
}
